import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MessageService } from 'primeng/api';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { LinkedInService } from 'src/app/services/linkedin.service';
import { EmailSequence } from '../sequence-metrics/sequence-metrics.component';
import { OutreachSequence } from 'src/app/models/linkedin.model';
import { SelectableCompany } from 'src/app/models/company.model';
import { CompaniesService } from 'src/app/services/companies.service';
import { Subject, takeUntil } from 'rxjs';
import { OutreachService } from 'src/app/services/outreach.service';

interface Sender {
  firstName: string;
  lastName: string;
  emailAddress: string;
  icon?: string;
  id: number;
}


interface ColumnMapping {
  field: string;
  required: boolean;
  selectedColumn: string | null;
}

interface PreviewData {
  Email: string;
  FirstName: string;
  LastName: string;
  Company: string;
  Position: string;
  Phone: string;
  LinkedIn: string;
  Website: string;
  error?: string | null;
}

@Component({
  selector: 'app-prospect-import-dialog',
  templateUrl: './prospect-import-dialog.component.html',
  styleUrls: ['./prospect-import-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    DropdownModule,
    ButtonModule,
    TableModule,
    ToastModule,
    SelectModule,
    InputTextModule,
  ],
  providers: [MessageService]
})
export class ProspectImportDialogComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  @Input() visible: boolean = false;
  @Input() platform = 'email';
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() fileUploaded = new EventEmitter < { file: File, mappings: { [key: string]: string }, listName: string, linkedInAccountId?: number, linkedInSenderEmail?: string, linkedInSenderName?:string,  sequenceId?: string }>();
  @Output() cancel = new EventEmitter<void>();
  @Input() company: SelectableCompany | null = null;

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  listName = '';
  titlePlatform = 'Email';
  selectedFile: File | null = null;
  csvContent: string = '';
  csvHeaders: string[] = [];

  selectedSender?: Sender | null = null;

  senders: Sender[] = [];
  sequences: OutreachSequence[] = [];
  selectedSequence: OutreachSequence | null = null;
  columnMappings: ColumnMapping[] = [
    { field: 'Email', required: true, selectedColumn: null },
    { field: 'FirstName', required: true, selectedColumn: null },
    { field: 'LastName', required: true, selectedColumn: null },
    { field: 'Company', required: false, selectedColumn: null },
    { field: 'Position', required: false, selectedColumn: null },
    { field: 'Phone', required: false, selectedColumn: null },
    { field: 'LinkedIn', required: false, selectedColumn: null },
    { field: 'Website', required: false, selectedColumn: null }
  ];

  previewData: PreviewData[] = [];
  totalRows: number = 0;
  displayedRows: number = 3;
  currentStep: 'upload' | 'mapping' = 'upload';

  constructor(
    private messageService: MessageService,
    private linkedinService: LinkedInService,
    private companiesService: CompaniesService,
    private emailOutreachService: OutreachService,
  ) { }

  ngOnInit() {
    if (this.platform == 'linkedin') {
      this.titlePlatform = 'Import LinkedIn Prospects';
      this.getSenders();
        
    } else {
      this.titlePlatform = 'Import Email Prospects'
    }
    // Watch for company changes and refresh sequences
    this.companiesService.selectedCompany$
      .pipe(takeUntil(this.destroy$))
      .subscribe(company => {
        if (company?.companyId && company.companyId !== this.company?.companyId) {
          this.company = company;
          this.getSequences();
        }
      });
  }


  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async getSenders() {
    try {
    const rep = await this.linkedinService.getLinkedinAccounts(this.company!.companyId);
      this.senders = rep.data;
      //console.log('SENDERS ',this.senders);
      
    } catch (e) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Unable to fetch a list of LinkedIn Senders at this time'
      });
    }
  }

  async getSequences() {
    try {
      let seq;
      
      if(this.platform=='linkedin') {
        //@ts-ignore
        seq = await this.linkedinService.getLinkedInSequences(this.company?.companyId);
        //@ts-ignore
        this.sequences = seq.data.filter(e => !e.linkedInAccountId)
      } else {
        seq = await this.emailOutreachService.getEmailSequences(this.company!.companyId);
        //@ts-ignore
        this.sequences = seq.data.sequences;
      }
      
    } catch (e) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Unable to fetch a list of LinkedIn Sequences at this time'
      });
    }
  }

  openFileBrowser(): void {
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: any) {
    const fileList: FileList = event.target.files;
    let file;
    if (fileList.length > 0) {
      file = fileList[0];
    }
    if (file?.type === 'text/csv' || file?.name.toLowerCase().endsWith('.csv')) {
      this.selectedFile = file;
      this.readCSVFile(file);
    } else {
      this.selectedFile = null;
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Please select a .csv file'
      });
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    if (event.dataTransfer?.files.length) {
      const file = event.dataTransfer.files[0];

      // Check if the file is a CSV
      if (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')) {
        this.selectedFile = file;
        this.readCSVFile(file);
      } else {
        // If not a CSV, clear selection and show error
        this.selectedFile = null;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Please select a .csv file'
        });
      }
    }
  }

  readCSVFile(file: File): void {
    const reader = new FileReader();

    reader.onload = (e) => {
      this.csvContent = reader.result as string;
      this.parseCSVHeaders();
      this.preparePreviewData();
      this.currentStep = 'mapping';
    };

    reader.readAsText(file);
  }

  parseCSVHeaders(): void {
    if (!this.csvContent) return;

    const lines = this.csvContent.split('\n');
    if (lines.length > 0) {
      const headerLine = lines[0];
      this.csvHeaders = headerLine.split(',').map(header => header.trim());
      console.log(this.csvHeaders);

      // Auto-match headers if possible
      this.autoMapColumns();
    }
  }

  autoMapColumns(): void {
    this.columnMappings.forEach(mapping => {
      // Try to find exact match
      const exactMatch = this.csvHeaders.find(
        header => header.toLowerCase() === mapping.field.toLowerCase()
      );

      if (exactMatch) {
        mapping.selectedColumn = exactMatch;
        return;
      }

      // Try to find partial match
      const partialMatch = this.csvHeaders.find(
        header => header.toLowerCase().includes(mapping.field.toLowerCase()) ||
          mapping.field.toLowerCase().includes(header.toLowerCase())
      );

      if (partialMatch) {
        mapping.selectedColumn = partialMatch;
      }
    });
  }

  preparePreviewData(): void {
    if (!this.csvContent) return;

    const lines = this.csvContent.split('\n');

    // Skip header row and get data rows
    const dataRows = lines.slice(1).filter(line => line.trim().length > 0);
    this.totalRows = dataRows.length;

    // Prepare preview data (first few rows)
    const rowsToDisplay = Math.min(this.displayedRows, dataRows.length);

    this.previewData = [];
    for (let i = 0; i < rowsToDisplay; i++) {
      const row = dataRows[i];
      const values = this.parseCSVRow(row);

      // Create a preview data object with error simulation
      this.previewData.push(this.createMappedPreview(this.columnMappings, values, this.csvHeaders));
    }
  }

  createMappedPreview(
    columnMappings: ColumnMapping[],
    rowValues: string[],
    headers: string[]
  ): PreviewData {
    // Initialize the preview object with empty strings for all fields
    const preview: PreviewData = {
      Email: '',
      FirstName: '',
      LastName: '',
      Company: '',
      Position: '',
      Phone: '',
      LinkedIn: '',
      Website: '',
      error: null
    };

    // Check if required fields have mappings
    const missingRequiredFields = columnMappings
      .filter(mapping => mapping.required && mapping.selectedColumn === null)
      .map(mapping => mapping.field);

    if (missingRequiredFields.length > 0) {
      preview.error = `Missing required field mappings: ${missingRequiredFields.join(', ')}`;
      return preview;
    }

    // Loop through each mapping
    columnMappings.forEach(mapping => {
      // If a column is selected for this field
      if (mapping.selectedColumn !== null) {
        // Find the index position of the selected column in the headers array
        const columnIndex = headers.indexOf(mapping.selectedColumn);

        // If the column was found in the headers
        if (columnIndex !== -1 && columnIndex < rowValues.length) {
          // Add the value to the preview using the field name as the key
          // Using type assertion to ensure TypeScript knows we're accessing a valid property
          (preview as any)[mapping.field] = rowValues[columnIndex];
        }
      }
    });

    return preview;
  }

  parseCSVRow(row: string): string[] {
    // This is a simple CSV parser, for production you might want to use a more robust one
    // that handles quoted values, commas within quoted strings, etc.
    return row.split(',').map(value => value.trim());
  }

  hideDialog() {
    this.visible = false;
    this.visibleChange.emit(this.visible);
    this.cancel.emit();
    this.resetDialog();
  }

  resetDialog(): void {
    this.selectedFile = null;
    this.csvContent = '';
    this.csvHeaders = [];
    this.previewData = [];
    this.currentStep = 'upload';
    this.selectedSender = null;
    this.selectedSequence = null;
    this.columnMappings.forEach(mapping => {
      mapping.selectedColumn = null;
    });
  }

  onBack(): void {
    this.currentStep = 'upload';
  }

  onImport(): void {
    if (!this.selectedFile) return;

    if (!this.listName || this.listName === '') {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Please enter a list name'
      });
      return;
    }

    if (this.platform == 'linkedin' && this.selectedSender == null) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Please select a LinkedIn Sender account'
      });
      return;
    }

   

    // Create mapping dictionary
    const mappings: { [key: string]: string } = {};
    this.columnMappings.forEach(mapping => {
      if (mapping.selectedColumn) {
        mappings[mapping.field] = mapping.selectedColumn;
      }
    });

    this.fileUploaded.emit({
      file: this.selectedFile,
      mappings: mappings,
      listName: this.listName,
      linkedInAccountId: this.selectedSender!=null? this.selectedSender?.id:undefined,
      linkedInSenderEmail: this.selectedSender!=null?this.selectedSender?.emailAddress:'',
      linkedInSenderName: this.selectedSender!=null?`${this.selectedSender!.firstName} ${this.selectedSender!.lastName}`:'',
      sequenceId: this.selectedSequence?.sequenceId || undefined
    });

    this.hideDialog();
  }

  areRequiredFieldsMapped(): boolean {
    return this.columnMappings
      .filter(mapping => mapping.required)
      .every(mapping => mapping.selectedColumn !== null);
  }
}